import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Star, BookOpen, Plus, Award, ExternalLink, Coins, Clock, ArrowRight, Rocket, Users, Calendar, CheckCircle, Share2, Copy, Megaphone } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

function getClubToken() { return localStorage.getItem('club_token'); }
function setClubAuth(token, name, email) {
  localStorage.setItem('club_token', token);
  localStorage.setItem('club_name', name);
  localStorage.setItem('club_email', email);
}

async function clubFetch(path, opts = {}) {
  const token = getClubToken();
  const res = await fetch(`${API}/api/club${path}`, {
    ...opts,
    headers: { 'Content-Type': 'application/json', 'X-Club-Token': token || '', ...opts.headers },
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.detail || 'Request failed');
  return data;
}

export default function ClubDashboard() {
  const [token, setToken] = useState(getClubToken());
  const [tab, setTab] = useState('launches');
  const [profile, setProfile] = useState(null);
  const [books, setBooks] = useState([]);
  const [myBooks, setMyBooks] = useState([]);
  const [myReviews, setMyReviews] = useState([]);
  const [launches, setLaunches] = useState([]);
  const [myLaunches, setMyLaunches] = useState([]);
  const [myClaims, setMyClaims] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Auth
  const [authMode, setAuthMode] = useState('join');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [authLoading, setAuthLoading] = useState(false);
  const [authMsg, setAuthMsg] = useState('');

  // Forms
  const [showSubmitBook, setShowSubmitBook] = useState(false);
  const [bookForm, setBookForm] = useState({ title: '', author: '', amazon_url: '', genre: '', description: '', credits_per_review: 10 });
  const [showCreateLaunch, setShowCreateLaunch] = useState(false);
  const [launchForm, setLaunchForm] = useState({ title: '', author_name: '', genre: '', description: '', launch_date: '', arc_slots: 20 });
  const [reviewUrl, setReviewUrl] = useState('');
  const [reviewBookId, setReviewBookId] = useState(null);
  const [claimModal, setClaimModal] = useState(null);
  const [claimMsg, setClaimMsg] = useState('');

  // Spread the Word
  const [generatedPosts, setGeneratedPosts] = useState(null);
  const [postInstructions, setPostInstructions] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [copied, setCopied] = useState(null);
  const [shareLogging, setShareLogging] = useState(null);
  const [todayShares, setTodayShares] = useState([]);
  const fetchData = useCallback(async () => {
    if (!token) return;
    try {
      const [profileData, booksData, myBooksData, myReviewsData, launchesData, myClaimsData, myLaunchesData] = await Promise.all([
        clubFetch('/profile'), clubFetch('/books'), clubFetch('/books/mine'),
        clubFetch('/reviews/mine'), clubFetch('/launches'),
        clubFetch('/claims/mine'), clubFetch('/launches/mine'),
      ]);
      setProfile(profileData);
      setBooks(booksData);
      setMyBooks(myBooksData);
      setMyReviews(myReviewsData);
      setLaunches(launchesData.campaigns || []);
      setMyClaims(myClaimsData.claims || []);
      setMyLaunches(myLaunchesData.campaigns || []);
    } catch {
      localStorage.removeItem('club_token');
      setToken(null);
    }
  }, [token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleAuth = async (e) => {
    e.preventDefault();
    setAuthLoading(true); setAuthMsg('');
    try {
      const endpoint = authMode === 'join' ? '/join' : '/login';
      const data = await clubFetch(endpoint, { method: 'POST', body: JSON.stringify({ name, email }) });
      setClubAuth(data.access_token, name, email);
      setToken(data.access_token);
      setAuthMsg(data.message || 'Welcome!');
    } catch (err) { setAuthMsg(err.message); }
    finally { setAuthLoading(false); }
  };

  const handleSubmitBook = async (e) => {
    e.preventDefault(); setLoading(true); setError('');
    try {
      await clubFetch('/books/submit', { method: 'POST', body: JSON.stringify({ ...bookForm, credits_per_review: parseInt(bookForm.credits_per_review) || 10 }) });
      setShowSubmitBook(false);
      setBookForm({ title: '', author: '', amazon_url: '', genre: '', description: '', credits_per_review: 10 });
      fetchData();
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  };

  const handleSubmitReview = async (bookId) => {
    if (!reviewUrl.trim()) return;
    setLoading(true); setError('');
    try {
      const data = await clubFetch('/reviews/submit', { method: 'POST', body: JSON.stringify({ book_id: bookId, amazon_review_url: reviewUrl }) });
      setReviewBookId(null); setReviewUrl('');
      alert(`Review verified! You earned ${data.credits_earned} credits.`);
      fetchData();
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  };

  const handleCreateLaunch = async (e) => {
    e.preventDefault(); setLoading(true); setError('');
    try {
      await clubFetch('/launches/create', { method: 'POST', body: JSON.stringify({ ...launchForm, arc_slots: parseInt(launchForm.arc_slots) || 20 }) });
      setShowCreateLaunch(false);
      setLaunchForm({ title: '', author_name: '', genre: '', description: '', launch_date: '', arc_slots: 20 });
      fetchData();
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  };

  const handleClaimLaunch = async (campaignId) => {
    if (!profile) return;
    setLoading(true); setClaimMsg('');
    try {
      const data = await clubFetch(`/launches/${campaignId}/claim`, {
        method: 'POST',
        body: JSON.stringify({ reader_email: profile.email, reader_name: profile.name })
      });
      setClaimMsg(data.message || 'Claimed!');
      setClaimModal(null);
      fetchData();
    } catch (err) { setClaimMsg(err.message); }
    finally { setLoading(false); }
  };

  // Auth screen
  if (!token) {
    return (
      <Layout>
        <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center px-4">
          <Card className="bg-gray-900/60 border-gray-800 max-w-md w-full">
            <CardContent className="p-8">
              <div className="text-center mb-8">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-500/10 border border-amber-500/20 text-amber-400 text-xs mb-4">
                  <Rocket className="w-3 h-3" /> The Authors' Exchange
                </div>
                <h1 className="text-3xl font-bold text-white mb-2" data-testid="club-auth-title">
                  {authMode === 'join' ? <>Join <span className="text-amber-400">The Exchange</span></> : <>Welcome <span className="text-amber-400">Back</span></>}
                </h1>
                <p className="text-sm text-gray-400">
                  {authMode === 'join' ? 'Free books, credits, and reviews. No password needed.' : 'Enter your name and email to sign in.'}
                </p>
              </div>
              <form onSubmit={handleAuth} className="space-y-4" data-testid="club-auth-form">
                <Input data-testid="club-name-input" placeholder="Your Name" value={name} onChange={e => setName(e.target.value)} required className="bg-gray-800 border-gray-700 text-white h-12" />
                <Input data-testid="club-email-input" type="email" placeholder="Email Address" value={email} onChange={e => setEmail(e.target.value)} required className="bg-gray-800 border-gray-700 text-white h-12" />
                <Button type="submit" disabled={authLoading} data-testid="club-auth-submit" className="w-full bg-amber-500 hover:bg-amber-600 text-black font-semibold h-12">
                  {authLoading ? 'Processing...' : authMode === 'join' ? 'Join Free — Get 50 Credits' : 'Access Dashboard'}
                </Button>
              </form>
              {authMsg && <p className="text-sm text-center mt-4 text-amber-400" data-testid="club-auth-msg">{authMsg}</p>}
              <p className="text-xs text-center text-gray-500 mt-6">
                {authMode === 'join'
                  ? <>Already a member? <button onClick={() => setAuthMode('login')} className="text-amber-400 hover:underline">Sign in</button></>
                  : <>New here? <button onClick={() => setAuthMode('join')} className="text-amber-400 hover:underline">Join free</button></>}
              </p>
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }

  const claimedIds = myClaims.map(c => c.campaign_id);

  // Dashboard
  return (
    <Layout>
      <div className="min-h-screen bg-[#0a0a0f]">
        {/* Header */}
        <section className="py-8 px-4 border-b border-gray-800">
          <div className="container mx-auto max-w-5xl flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold text-white" data-testid="club-dash-title">
                Welcome, <span className="text-amber-400">{profile?.name || '...'}</span>
              </h1>
              {profile?.badge && <span className="inline-flex items-center gap-1 text-xs font-bold mt-1" style={{ color: profile.badge.color }} data-testid="club-badge"><Award className="w-3 h-3" /> {profile.badge.name}</span>}
            </div>
            <div className="flex items-center gap-6">
              <div className="text-center" data-testid="club-credits"><p className="text-2xl font-bold text-amber-400">{profile?.credits ?? '—'}</p><p className="text-[10px] uppercase tracking-wider text-gray-500">Credits</p></div>
              <div className="text-center"><p className="text-2xl font-bold text-white">{profile?.total_reviews ?? 0}</p><p className="text-[10px] uppercase tracking-wider text-gray-500">Reviews</p></div>
              <div className="text-center"><p className="text-2xl font-bold text-white">{profile?.total_books ?? 0}</p><p className="text-[10px] uppercase tracking-wider text-gray-500">Books</p></div>
            </div>
          </div>
        </section>

        <div className="container mx-auto max-w-5xl py-6 px-4">
          {error && <p className="text-sm text-red-400 mb-4 bg-red-500/10 border border-red-500/20 rounded-lg p-3" data-testid="club-error">{error}</p>}

          {/* Tabs */}
          <div className="flex gap-1 mb-8 border-b border-gray-800 overflow-x-auto" data-testid="club-tabs">
            {[
              { id: 'launches', label: 'Free Books', icon: Rocket },
              { id: 'review', label: 'Review Queue', icon: Star },
              { id: 'mybooks', label: 'My Books', icon: BookOpen },
              { id: 'myreviews', label: 'My Reviews', icon: Clock },
              { id: 'mylaunches', label: 'My Launches', icon: Rocket },
              { id: 'spread', label: 'Spread the Word', icon: Megaphone },
            ].map(t => (
              <button key={t.id} onClick={() => setTab(t.id)} data-testid={`club-tab-${t.id}`}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-semibold border-b-2 whitespace-nowrap transition-colors ${tab === t.id ? 'border-amber-400 text-amber-400' : 'border-transparent text-gray-500 hover:text-white'}`}>
                <t.icon className="w-4 h-4" /> {t.label}
              </button>
            ))}
          </div>

          {/* ── Free Books / ARC Launches Tab ── */}
          {tab === 'launches' && (
            <div>
              {launches.length === 0 ? (
                <div className="text-center py-16 bg-gray-900/50 border border-gray-800 rounded-lg">
                  <Rocket className="w-10 h-10 text-amber-400/30 mx-auto mb-4" />
                  <p className="text-gray-400 mb-2">No launch campaigns available right now.</p>
                  <p className="text-xs text-gray-600">Check back soon or create your own!</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {launches.map(c => {
                    const slotsLeft = (c.arc_slots || 20) - (c.slots_claimed || 0);
                    const claimed = claimedIds.includes(c.campaign_id);
                    return (
                      <Card key={c.campaign_id} className="bg-gray-900/50 border-gray-800" data-testid={`launch-${c.campaign_id}`}>
                        <CardContent className="p-5">
                          <div className="flex justify-between items-start mb-2">
                            <div>
                              <span className="text-xs text-amber-400 bg-amber-500/10 px-2 py-0.5 rounded">{c.genre}</span>
                              <h3 className="text-lg font-semibold text-white mt-1">{c.title}</h3>
                              <p className="text-xs text-gray-500">by {c.author_name}</p>
                            </div>
                            <span className="text-xs text-gray-500 flex items-center gap-1"><Users className="w-3 h-3" /> {slotsLeft > 0 ? `${slotsLeft} left` : 'Full'}</span>
                          </div>
                          <p className="text-sm text-gray-400 line-clamp-2 mb-3">{c.description}</p>
                          <div className="flex items-center justify-between">
                            <span className="text-xs text-gray-500 flex items-center gap-1"><Calendar className="w-3 h-3" /> Launch: {new Date(c.launch_date).toLocaleDateString()}</span>
                            {claimed ? (
                              <span className="text-green-400 text-sm flex items-center gap-1"><CheckCircle className="w-4 h-4" /> Claimed</span>
                            ) : slotsLeft <= 0 ? (
                              <span className="text-gray-500 text-sm">Full</span>
                            ) : (
                              <Button onClick={() => handleClaimLaunch(c.campaign_id)} size="sm" data-testid={`claim-launch-${c.campaign_id}`}
                                className="bg-amber-500 hover:bg-amber-600 text-black font-semibold">
                                Claim Free Copy
                              </Button>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              )}
              {/* My Claimed ARCs */}
              {myClaims.length > 0 && (
                <div className="mt-8">
                  <h3 className="text-sm font-semibold text-gray-300 mb-3">My Claimed Books ({myClaims.length})</h3>
                  <div className="space-y-2">
                    {myClaims.map(cl => (
                      <Card key={cl.claim_id} className="bg-gray-900/30 border-gray-800/50">
                        <CardContent className="p-4 flex items-center justify-between">
                          <div>
                            <p className="text-white font-medium">{cl.book_title}</p>
                            <p className="text-xs text-gray-500">Claimed: {new Date(cl.claimed_at).toLocaleDateString()} &middot; Launch: {cl.launch_date ? new Date(cl.launch_date).toLocaleDateString() : 'TBD'}</p>
                          </div>
                          <span className={`text-xs font-semibold ${cl.status === 'reviewed' ? 'text-green-400' : 'text-yellow-400'}`}>
                            {cl.status === 'reviewed' ? 'Reviewed' : 'Pending Review'}
                          </span>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── Review Queue Tab ── */}
          {tab === 'review' && (
            <div>
              {books.filter(b => b.member_email !== profile?.email).length === 0 ? (
                <div className="text-center py-16 bg-gray-900/50 border border-gray-800 rounded-lg">
                  <BookOpen className="w-10 h-10 text-amber-400/30 mx-auto mb-4" />
                  <p className="text-gray-400 mb-2">No books in the review queue yet.</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {books.filter(b => b.member_email !== profile?.email).map(book => (
                    <Card key={book.id} className="bg-gray-900/50 border-gray-800" data-testid={`club-book-${book.id}`}>
                      <CardContent className="p-5">
                        <div className="flex justify-between items-start mb-3">
                          <div><h3 className="text-lg font-semibold text-white">{book.title}</h3><p className="text-xs text-gray-500">by {book.author} &middot; {book.genre}</p></div>
                          <span className="flex items-center gap-1 text-xs font-bold text-amber-400 bg-amber-500/10 px-2 py-1 rounded"><Coins className="w-3 h-3" /> +{book.credits_per_review}</span>
                        </div>
                        <p className="text-sm text-gray-400 mb-4 line-clamp-2">{book.description}</p>
                        <div className="flex items-center gap-3">
                          <a href={book.amazon_url} target="_blank" rel="noopener noreferrer" className="text-xs text-amber-400 hover:underline flex items-center gap-1">View on Amazon <ExternalLink className="w-3 h-3" /></a>
                          {reviewBookId === book.id ? (
                            <div className="flex-1 flex gap-2">
                              <Input type="text" placeholder="Paste Amazon review URL" value={reviewUrl} onChange={e => setReviewUrl(e.target.value)} data-testid="review-url-input" className="flex-1 h-9 text-xs bg-gray-800 border-gray-700 text-white" />
                              <Button onClick={() => handleSubmitReview(book.id)} disabled={loading} data-testid="review-submit-btn" size="sm" className="bg-amber-500 hover:bg-amber-600 text-black font-semibold h-9">{loading ? '...' : 'Submit'}</Button>
                              <Button onClick={() => { setReviewBookId(null); setReviewUrl(''); }} variant="ghost" size="sm" className="text-gray-400 h-9">Cancel</Button>
                            </div>
                          ) : (
                            <Button onClick={() => setReviewBookId(book.id)} variant="outline" size="sm" data-testid={`review-btn-${book.id}`} className="border-gray-700 text-gray-300 hover:border-amber-400 hover:text-amber-400 text-xs"><Star className="w-3 h-3 mr-1" /> I Reviewed This</Button>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ── My Books Tab ── */}
          {tab === 'mybooks' && (
            <div>
              <div className="flex justify-between items-center mb-6">
                <p className="text-sm text-gray-400">{myBooks.length} book{myBooks.length !== 1 ? 's' : ''} submitted</p>
                <Button onClick={() => setShowSubmitBook(!showSubmitBook)} data-testid="club-submit-book-btn" className="bg-amber-500 hover:bg-amber-600 text-black font-semibold"><Plus className="w-4 h-4 mr-1" /> Submit a Book</Button>
              </div>
              {showSubmitBook && (
                <Card className="bg-gray-900/50 border-amber-500/20 mb-6"><CardContent className="p-6">
                  <form onSubmit={handleSubmitBook} className="space-y-4" data-testid="club-book-form">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <Input placeholder="Book Title" value={bookForm.title} onChange={e => setBookForm({...bookForm, title: e.target.value})} required data-testid="book-title-input" className="bg-gray-800 border-gray-700 text-white h-11" />
                      <Input placeholder="Author Name" value={bookForm.author} onChange={e => setBookForm({...bookForm, author: e.target.value})} required data-testid="book-author-input" className="bg-gray-800 border-gray-700 text-white h-11" />
                    </div>
                    <Input type="url" placeholder="Amazon Book URL" value={bookForm.amazon_url} onChange={e => setBookForm({...bookForm, amazon_url: e.target.value})} required data-testid="book-url-input" className="bg-gray-800 border-gray-700 text-white h-11" />
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <select value={bookForm.genre} onChange={e => setBookForm({...bookForm, genre: e.target.value})} required data-testid="book-genre-select" className="h-11 px-4 bg-gray-800 border border-gray-700 rounded-md text-white text-sm">
                        <option value="">Select Genre</option>
                        {['Fiction','Non-Fiction','Mystery','Romance','Sci-Fi','Fantasy','Thriller','Biography','Self-Help','Business','History','Poetry','Children','Young Adult','Horror','Other'].map(g => <option key={g} value={g}>{g}</option>)}
                      </select>
                      <div className="flex items-center gap-3"><label className="text-xs text-gray-400 whitespace-nowrap">Credits/review:</label><Input type="number" min="5" max="50" value={bookForm.credits_per_review} onChange={e => setBookForm({...bookForm, credits_per_review: e.target.value})} data-testid="book-credits-input" className="bg-gray-800 border-gray-700 text-white h-11 w-24 text-center" /></div>
                    </div>
                    <textarea placeholder="Brief description..." value={bookForm.description} onChange={e => setBookForm({...bookForm, description: e.target.value})} required rows={3} data-testid="book-desc-input" className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-md text-white placeholder:text-gray-500 text-sm resize-none" />
                    <Button type="submit" disabled={loading} data-testid="book-submit-btn" className="bg-amber-500 hover:bg-amber-600 text-black font-semibold">{loading ? 'Submitting...' : 'Submit Book for Reviews'}</Button>
                  </form>
                </CardContent></Card>
              )}
              {myBooks.length === 0 && !showSubmitBook ? (
                <div className="text-center py-16 bg-gray-900/50 border border-gray-800 rounded-lg"><BookOpen className="w-10 h-10 text-amber-400/30 mx-auto mb-4" /><p className="text-gray-400 mb-2">No books submitted yet.</p><button onClick={() => setShowSubmitBook(true)} className="text-sm text-amber-400 hover:underline">Submit your first book</button></div>
              ) : (
                <div className="space-y-3">{myBooks.map(b => (
                  <Card key={b.id} className="bg-gray-900/50 border-gray-800" data-testid={`mybook-${b.id}`}><CardContent className="p-5 flex items-center justify-between gap-3">
                    <div><h3 className="text-base font-semibold text-white">{b.title}</h3><p className="text-xs text-gray-500">{b.genre} &middot; {b.reviews_received} review{b.reviews_received !== 1 ? 's' : ''} &middot; {b.credits_per_review} credits/review</p></div>
                    <a href={b.amazon_url} target="_blank" rel="noopener noreferrer" className="text-xs text-amber-400 hover:underline flex items-center gap-1 shrink-0">Amazon <ExternalLink className="w-3 h-3" /></a>
                  </CardContent></Card>
                ))}</div>
              )}
            </div>
          )}

          {/* ── My Reviews Tab ── */}
          {tab === 'myreviews' && (
            <div>
              {myReviews.length === 0 ? (
                <div className="text-center py-16 bg-gray-900/50 border border-gray-800 rounded-lg"><Star className="w-10 h-10 text-amber-400/30 mx-auto mb-4" /><p className="text-gray-400 mb-2">No reviews yet.</p><button onClick={() => setTab('review')} className="text-sm text-amber-400 hover:underline">Browse books to review</button></div>
              ) : (
                <div className="space-y-3">{myReviews.map(r => (
                  <Card key={r.id} className="bg-gray-900/50 border-gray-800" data-testid={`myreview-${r.id}`}><CardContent className="p-5 flex items-center justify-between gap-3">
                    <div><h3 className="text-base font-semibold text-white">{r.book_title}</h3><p className="text-xs text-gray-500"><span className="text-green-400 font-semibold">+{r.credits_awarded} credits</span> &middot; {new Date(r.submitted_at).toLocaleDateString()}</p></div>
                    <a href={r.amazon_review_url} target="_blank" rel="noopener noreferrer" className="text-xs text-amber-400 hover:underline flex items-center gap-1 shrink-0">View Review <ExternalLink className="w-3 h-3" /></a>
                  </CardContent></Card>
                ))}</div>
              )}
            </div>
          )}

          {/* ── My Launches Tab (Author) ── */}
          {tab === 'mylaunches' && (
            <div>
              <div className="flex justify-between items-center mb-6">
                <p className="text-sm text-gray-400">{myLaunches.length} campaign{myLaunches.length !== 1 ? 's' : ''}</p>
                <Button onClick={() => setShowCreateLaunch(!showCreateLaunch)} data-testid="create-launch-btn" className="bg-amber-500 hover:bg-amber-600 text-black font-semibold"><Plus className="w-4 h-4 mr-1" /> New Launch</Button>
              </div>
              {showCreateLaunch && (
                <Card className="bg-gray-900/50 border-amber-500/20 mb-6"><CardContent className="p-6">
                  <form onSubmit={handleCreateLaunch} className="space-y-4" data-testid="launch-form">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <Input placeholder="Book Title" value={launchForm.title} onChange={e => setLaunchForm({...launchForm, title: e.target.value})} required data-testid="launch-title-input" className="bg-gray-800 border-gray-700 text-white h-11" />
                      <Input placeholder="Author Name" value={launchForm.author_name} onChange={e => setLaunchForm({...launchForm, author_name: e.target.value})} required data-testid="launch-author-input" className="bg-gray-800 border-gray-700 text-white h-11" />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                      <Input placeholder="Genre" value={launchForm.genre} onChange={e => setLaunchForm({...launchForm, genre: e.target.value})} required data-testid="launch-genre-input" className="bg-gray-800 border-gray-700 text-white h-11" />
                      <Input type="date" value={launchForm.launch_date} onChange={e => setLaunchForm({...launchForm, launch_date: e.target.value})} required data-testid="launch-date-input" className="bg-gray-800 border-gray-700 text-white h-11" />
                      <Input type="number" min="5" max="100" placeholder="ARC Slots" value={launchForm.arc_slots} onChange={e => setLaunchForm({...launchForm, arc_slots: e.target.value})} data-testid="launch-slots-input" className="bg-gray-800 border-gray-700 text-white h-11" />
                    </div>
                    <textarea placeholder="Tell readers about your book..." value={launchForm.description} onChange={e => setLaunchForm({...launchForm, description: e.target.value})} required rows={3} data-testid="launch-desc-input" className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-md text-white placeholder:text-gray-500 text-sm resize-none" />
                    <Button type="submit" disabled={loading} data-testid="launch-submit-btn" className="bg-amber-500 hover:bg-amber-600 text-black font-semibold">{loading ? 'Creating...' : 'Create Launch Campaign'}</Button>
                  </form>
                </CardContent></Card>
              )}
              {myLaunches.length === 0 && !showCreateLaunch ? (
                <div className="text-center py-16 bg-gray-900/50 border border-gray-800 rounded-lg"><Rocket className="w-10 h-10 text-amber-400/30 mx-auto mb-4" /><p className="text-gray-400 mb-2">No launch campaigns yet.</p><button onClick={() => setShowCreateLaunch(true)} className="text-sm text-amber-400 hover:underline">Create your first launch</button></div>
              ) : (
                <div className="space-y-3">{myLaunches.map(c => (
                  <Card key={c.campaign_id} className="bg-gray-900/50 border-gray-800" data-testid={`mylaunch-${c.campaign_id}`}><CardContent className="p-5">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="flex items-center gap-2"><h3 className="text-base font-semibold text-white">{c.title}</h3>
                          <span className={`text-xs px-2 py-0.5 rounded ${c.status === 'accepting_readers' ? 'bg-green-500/10 text-green-400' : 'bg-gray-500/10 text-gray-400'}`}>{c.status === 'accepting_readers' ? 'Active' : c.status}</span></div>
                        <p className="text-xs text-gray-500">{c.genre} &middot; Launch: {new Date(c.launch_date).toLocaleDateString()} &middot; {c.slots_claimed || 0}/{c.arc_slots} claimed &middot; {c.reviews_received || 0} reviews</p>
                      </div>
                    </div>
                  </CardContent></Card>
                ))}</div>
              )}
            </div>
          )}

          {/* ── Spread the Word Tab ── */}
          {tab === 'spread' && (
            <div>
              {/* Incentive Banner */}
              <Card className="bg-gradient-to-r from-amber-500/10 to-orange-500/10 border-amber-500/20 mb-6">
                <CardContent className="p-5 flex items-center justify-between flex-wrap gap-4">
                  <div>
                    <h3 className="text-white font-semibold flex items-center gap-2"><Share2 className="w-4 h-4 text-amber-400" /> Earn Credits by Spreading the Word</h3>
                    <p className="text-sm text-gray-400 mt-1">Share on any platform → <span className="text-amber-400 font-semibold">+10 credits</span>. Share on 3+ platforms in a day → <span className="text-amber-400 font-semibold">+10 bonus</span></p>
                  </div>
                  {!generatedPosts && (
                    <Button onClick={async () => {
                      setGenerating(true); setError('');
                      try {
                        const data = await clubFetch('/generate-posts', { method: 'POST', body: JSON.stringify({ context: 'general' }) });
                        setGeneratedPosts(data.posts);
                        setPostInstructions(data.instructions);
                      } catch (err) { setError(err.message); }
                      finally { setGenerating(false); }
                    }} disabled={generating} data-testid="generate-posts-btn" className="bg-amber-500 hover:bg-amber-600 text-black font-semibold shrink-0">
                      {generating ? 'Generating...' : 'Generate Posts with AI'}
                    </Button>
                  )}
                </CardContent>
              </Card>

              {generating && (
                <div className="text-center py-16">
                  <div className="inline-block animate-spin rounded-full h-10 w-10 border-b-2 border-amber-500 mb-4" />
                  <p className="text-gray-400">AI is crafting personalized posts for each platform...</p>
                </div>
              )}

              {generatedPosts && postInstructions && (
                <div className="space-y-4">
                  {Object.entries(generatedPosts).map(([platform, post]) => {
                    const info = postInstructions[platform];
                    if (!info) return null;
                    const postText = platform === 'reddit' ? `${post.title || ''}\n\n${post.body || ''}` : (post.body || post.title || '');
                    const isShared = todayShares.includes(platform);
                    return (
                      <Card key={platform} className="bg-gray-900/50 border-gray-800" data-testid={`spread-${platform}`}>
                        <CardContent className="p-5">
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-2">
                              <span className="text-xs font-bold text-amber-400 bg-amber-500/10 px-2 py-0.5 rounded">{info.name}</span>
                              <span className="text-xs text-gray-500">+10 credits</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Button onClick={() => { navigator.clipboard.writeText(postText); setCopied(platform); setTimeout(() => setCopied(null), 2000); }}
                                size="sm" variant="outline" data-testid={`copy-${platform}`}
                                className={`text-xs ${copied === platform ? 'border-green-500 text-green-400' : 'border-gray-700 text-gray-300 hover:border-amber-400 hover:text-amber-400'}`}>
                                {copied === platform ? <><CheckCircle className="w-3 h-3 mr-1" /> Copied</> : <><Copy className="w-3 h-3 mr-1" /> Copy</>}
                              </Button>
                              {isShared ? (
                                <span className="text-xs text-green-400 flex items-center gap-1"><CheckCircle className="w-3 h-3" /> Shared</span>
                              ) : (
                                <Button onClick={async () => {
                                  setShareLogging(platform);
                                  try {
                                    const data = await clubFetch('/log-share', { method: 'POST', body: JSON.stringify({ platform, post_url: '' }) });
                                    setTodayShares(prev => [...prev, platform]);
                                    alert(data.message);
                                    fetchData();
                                  } catch (err) { alert(err.message); }
                                  finally { setShareLogging(null); }
                                }} disabled={shareLogging === platform} size="sm" data-testid={`share-${platform}`}
                                  className="bg-amber-500 hover:bg-amber-600 text-black font-semibold text-xs">
                                  {shareLogging === platform ? '...' : 'I Posted This'}
                                </Button>
                              )}
                            </div>
                          </div>

                          {/* Generated Post */}
                          {post.title && platform !== 'twitter' && <p className="text-sm font-semibold text-white mb-2">{post.title}</p>}
                          <pre className="text-sm text-gray-300 whitespace-pre-wrap bg-gray-800/50 rounded p-4 mb-3 max-h-40 overflow-y-auto font-sans">{post.body || ''}</pre>

                          {/* Instructions */}
                          <details className="group">
                            <summary className="text-xs text-gray-500 cursor-pointer hover:text-gray-300 flex items-center gap-1">
                              <span className="group-open:hidden">Show posting instructions</span>
                              <span className="hidden group-open:inline">Hide instructions</span>
                            </summary>
                            <div className="mt-3 p-3 bg-gray-800/30 rounded text-xs text-gray-400 space-y-2">
                              <p className="font-semibold text-gray-300">Steps:</p>
                              <ol className="list-decimal list-inside space-y-1">
                                {info.steps.map((step, i) => <li key={i}>{step}</li>)}
                              </ol>
                              <p className="mt-2"><span className="font-semibold text-gray-300">Rules:</span> {info.rules}</p>
                              <p><span className="font-semibold text-gray-300">Where to post:</span> {info.targets.join(', ')}</p>
                            </div>
                          </details>
                        </CardContent>
                      </Card>
                    );
                  })}

                  <div className="text-center mt-6">
                    <Button onClick={() => { setGeneratedPosts(null); setPostInstructions(null); }} variant="outline" className="border-gray-700 text-gray-400 hover:text-white text-sm" data-testid="regenerate-btn">
                      Generate New Posts
                    </Button>
                  </div>
                </div>
              )}

              {!generatedPosts && !generating && (
                <div className="text-center py-16 bg-gray-900/50 border border-gray-800 rounded-lg">
                  <Megaphone className="w-10 h-10 text-amber-400/30 mx-auto mb-4" />
                  <p className="text-gray-400 mb-2">Help grow The Exchange and earn credits</p>
                  <p className="text-xs text-gray-600">Click "Generate Posts with AI" to get personalized, ready-to-post content for 5 platforms</p>
                </div>
              )}
            </div>
          )}

          {/* Footer links */}
          <div className="mt-12 flex flex-wrap gap-3">
            <Link to="/club/leaderboard" className="text-xs text-amber-400 hover:underline flex items-center gap-1" data-testid="club-leaderboard-link"><Award className="w-3 h-3" /> Leaderboard</Link>
            <Link to="/club" className="text-xs text-gray-400 hover:text-white flex items-center gap-1"><ArrowRight className="w-3 h-3" /> About The Exchange</Link>
            <button onClick={() => { localStorage.removeItem('club_token'); setToken(null); }} className="text-xs text-gray-500 hover:text-white ml-auto" data-testid="club-logout">Sign Out</button>
          </div>
        </div>
      </div>
    </Layout>
  );
}
